import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import AutoGraphRounded from '@mui/icons-material/AutoGraphRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import Groups2Rounded from '@mui/icons-material/Groups2Rounded';
import LightbulbRounded from '@mui/icons-material/LightbulbRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import TrendingDownRounded from '@mui/icons-material/TrendingDownRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { LineChart } from '@mui/x-charts/LineChart';
import { getStudentAvatar } from '../../shared/studentAvatar';
import { getMeCached } from '../authCache';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import { deleteWeeklyReadingResult, fetchWeeklyReadingAssignment, fetchWeeklyReadingOverview } from '../api/miniTasks';
import './WeeklyReadingTrackingPage.css';
import './WeeklyReadingTypographyV1.css';

const DEFAULT_READING_IMAGE = '/mini-tasks/weekly-reading/default-reading-hero.png';
const analysisFailureLabels = {
  accuracy_too_low: 'Yeterli okuma algılanamadı',
  empty_transcript: 'Konuşma algılanamadı',
  provider_not_configured: 'Analiz hizmeti kullanılamıyor',
  processing_failed: 'Analiz tamamlanamadı',
};

const statusLabels = {
  pending: 'Bekliyor',
  opened: 'Açtı',
  in_progress: 'Okuma başlatıldı',
  retry_required: 'Yeniden denemeli',
  completed: 'Tamamladı',
  suspicious: 'Kontrol gerekli',
};

function readingStatusLabel(status, activity) {
  if (status === 'completed') return statusLabels.completed;
  if (activity === 'interrupted') return 'Yarım kaldı';
  return statusLabels[status] || status;
}

function formatDate(value, options = {}) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'short',
    ...(options.withYear === false ? {} : { year: 'numeric' }),
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(date);
}

function formatDuration(milliseconds) {
  if (!milliseconds) return '—';
  const totalSeconds = Math.round(Number(milliseconds) / 1000);
  return `${Math.floor(totalSeconds / 60)} dk ${totalSeconds % 60} sn`;
}

function formatAccuracy(value, analysisStatus, failureCode) {
  if (value != null) return `%${Number(value).toFixed(1).replace('.0', '')}`;
  if (analysisStatus === 'uploaded' || analysisStatus === 'processing') return 'Analiz ediliyor';
  if (analysisStatus === 'failed') return analysisFailureLabels[failureCode] || 'Analiz tamamlanamadı';
  return '—';
}

function average(values) {
  const valid = values.filter((value) => value != null && Number.isFinite(Number(value))).map(Number);
  if (!valid.length) return null;
  return Number((valid.reduce((total, value) => total + value, 0) / valid.length).toFixed(1));
}

function classLabel(item) {
  return String(item?.name || item?.code || item?.id || '').trim();
}

function formatMetricNumber(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return Number(value).toFixed(1).replace('.0', '');
}

function buildPerformanceComparison({
  metricName,
  currentValue,
  historicalValues,
  lastWeekValue,
  threshold,
  unit,
  lowThreshold,
  positiveTone = 'blue',
  baselineNoun = 'okuma',
}) {
  if (currentValue == null) {
    return {
      main: `${metricName} için henüz geçerli ölçüm yok.`,
      helper: 'Ölçüm tamamlandığında karşılaştırma burada görünecek.',
      delta: null,
      baselineLabel: 'geçmiş ölçümlere göre',
      tone: 'blue',
    };
  }

  const validHistory = historicalValues
    .filter((value) => value != null && Number.isFinite(Number(value)))
    .map(Number);
  const hasRollingBaseline = validHistory.length >= 3;
  const rollingHistory = validHistory.slice(0, 4);
  const baseline = hasRollingBaseline ? average(rollingHistory) : lastWeekValue;

  if (baseline == null) {
    return {
      main: `${metricName} için karşılaştırılabilir geçmiş veri yok.`,
      helper: 'Yeni okumalar tamamlandıkça eğilim oluşacak.',
      delta: null,
      baselineLabel: 'geçmiş ölçümlere göre',
      tone: 'blue',
    };
  }

  const delta = Number((Number(currentValue) - Number(baseline)).toFixed(1));
  const absoluteDelta = formatMetricNumber(Math.abs(delta));
  const baselineText = hasRollingBaseline ? `önceki ${rollingHistory.length} ${baselineNoun}nın ortalamasına` : 'geçen haftaki sonuca';
  const possessiveBaselineText = hasRollingBaseline ? `önceki ${rollingHistory.length} ${baselineNoun}nın ortalamasının` : 'geçen haftanın';
  let main;

  if (lowThreshold != null && Number(currentValue) < lowThreshold) {
    main = `${metricName} %${formatMetricNumber(currentValue)}; kısa bir kontrol faydalı olabilir.`;
  } else if (Math.abs(delta) < threshold) {
    main = `${metricName} ${baselineText} yakın.`;
  } else {
    main = `${metricName} ${possessiveBaselineText} ${absoluteDelta} ${unit} ${delta > 0 ? 'üzerinde' : 'altında'}.`;
  }

  let helper;
  if (!hasRollingBaseline) {
    helper = 'Ortalama karşılaştırması için en az 3 geçmiş ölçüm gerekir.';
  } else if (lastWeekValue == null) {
    helper = 'Geçen hafta karşılaştırması için geçerli ölçüm yok.';
  } else {
    const lastWeekDelta = Number((Number(currentValue) - Number(lastWeekValue)).toFixed(1));
    helper = Math.abs(lastWeekDelta) < threshold
      ? 'Geçen haftaya göre benzer seviyede.'
      : `Geçen haftaya göre ${lastWeekDelta > 0 ? '+' : ''}${formatMetricNumber(lastWeekDelta)} ${unit}.`;
  }

  return {
    main,
    helper,
    delta,
    baselineLabel: hasRollingBaseline ? `önceki ${rollingHistory.length} ${baselineNoun}nın ortalamasına göre` : 'geçen haftaya göre',
    tone: (lowThreshold != null && Number(currentValue) < lowThreshold) || delta < -threshold ? 'orange' : positiveTone,
  };
}

function getAssignmentAccuracy(overview, assignmentId) {
  return average((overview?.student_trends || []).flatMap((student) => (
    student.points || []
  )).filter((point) => String(point.assignment_id) === String(assignmentId)).map((point) => point.accuracy_pct));
}

function getPreviousAverage(points, currentAssignmentId, key) {
  return average((points || []).filter((point) => (
    String(point.assignment_id) !== String(currentAssignmentId) && point[key] != null
  )).slice(-3).map((point) => point[key]));
}

function buildStudentView(trend, assignmentId, detailStudent = null) {
  const points = trend?.points || [];
  const point = points.find((item) => String(item.assignment_id) === String(assignmentId));
  const source = detailStudent || point || {};
  const status = source.status || 'pending';
  const speed = source.words_per_minute ?? point?.words_per_minute ?? null;
  const accuracy = source.accuracy_pct ?? point?.accuracy_pct ?? null;
  const previousSpeed = getPreviousAverage(points, assignmentId, 'words_per_minute');
  let insight = 'Düzenli';
  let insightTone = 'success';
  let note = 'Okuma performansı beklenen aralıkta görünüyor.';

  if (status !== 'completed' && source.activity_status === 'interrupted') {
    insight = 'Yarım kaldı';
    insightTone = 'neutral';
    note = 'Okuma tamamlanmadı; aynı metne yeniden başlanabilir.';
  } else if (status === 'in_progress') {
    insight = 'Okuma başlatıldı';
    insightTone = 'neutral';
    note = 'Bu okuma için henüz tamamlanma kaydı yok.';
  } else if (!point || status === 'pending') {
    insight = 'Henüz okumadı';
    insightTone = 'neutral';
    note = 'Bu haftaki pasaj için tamamlanma kaydı yok.';
  } else if (status === 'retry_required' || status === 'suspicious') {
    insight = status === 'retry_required' ? 'Yeniden denemeli' : 'Kontrol gerekli';
    insightTone = 'warning';
    note = 'Kısa bir öğretmen kontrolü faydalı olabilir.';
  } else if (accuracy != null && Number(accuracy) < 80) {
    insight = 'Doğruluk düşük';
    insightTone = 'warning';
    note = `Doğruluk oranı ${formatAccuracy(accuracy)}.`;
  } else if (speed != null && previousSpeed != null && Number(speed) < previousSpeed - Math.max(5, previousSpeed * 0.1)) {
    const change = Number((Number(speed) - previousSpeed).toFixed(1));
    insight = 'Hızı düştü';
    insightTone = 'danger';
    note = `Yakın geçmiş ortalamasına göre ${change} k/dk.`;
  } else if (speed != null && previousSpeed != null && Number(speed) > previousSpeed + 3) {
    insight = 'İlerleme güzel';
    insightTone = 'success';
    note = 'Yakın geçmiş ölçümlerine göre olumlu ilerliyor.';
  }

  return {
    id: trend?.student_id ?? detailStudent?.student_id,
    studentAssignmentId: source.student_assignment_id,
    studentNo: trend?.student_no ?? detailStudent?.student_no,
    name: trend?.student_name || detailStudent?.student_name || 'Öğrenci',
    status,
    activityStatus: source.activity_status,
    speed,
    accuracy,
    duration: formatDuration(source.elapsed_ms),
    completedAt: formatDateTime(source.completed_at),
    analysisStatus: source.analysis_status,
    analysisFailureCode: source.analysis_failure_code,
    insight,
    insightTone,
    note,
    point,
    points,
  };
}

function BookMark() {
  return <Box className="wr-book-mark" aria-hidden="true"><span /><span /></Box>;
}

function StudentAvatar({ student, size = 44 }) {
  const theme = getStudentAvatar(student.id);
  return (
    <Avatar
      className="wr-student-avatar"
      sx={{ width: size, height: size, bgcolor: theme.bgColor, borderColor: theme.borderColor, fontSize: size * 0.52 }}
      aria-label={`${student.name} hayvan avatarı`}
    >
      {theme.avatar}
    </Avatar>
  );
}

function StatusPill({ student }) {
  const icon = student.insightTone === 'danger' ? <TrendingDownRounded fontSize="inherit" />
    : student.insightTone === 'warning' ? <WarningAmberRounded fontSize="inherit" />
      : student.insightTone === 'success' ? <TrendingUpRounded fontSize="inherit" />
        : ['started', 'interrupted'].includes(student.activityStatus) ? <AccessTimeRounded fontSize="inherit" />
        : <TaskAltRounded fontSize="inherit" />;
  return <Chip className={`wr-status wr-status--${student.insightTone}`} icon={icon} size="small" label={student.insight} />;
}

function MiniMetric({ icon, tone, value, label }) {
  return (
    <Box className="wr-mini-metric">
      <Box className={`wr-icon-bubble wr-icon-bubble--${tone}`}>{icon}</Box>
      <Box>
        <Typography className="wr-mini-metric__value">{value}</Typography>
        <Typography className="wr-mini-metric__label">{label}</Typography>
      </Box>
    </Box>
  );
}

function InsightCard({ icon, tone, eyebrow, children, helper, action }) {
  return (
    <Box className={`wr-insight-card wr-insight-card--${tone}`}>
      <Box className={`wr-insight-card__icon wr-insight-card__icon--${tone}`}>{icon}</Box>
      <Box>
        <Typography className={`wr-insight-card__eyebrow wr-text--${tone}`}>{eyebrow}</Typography>
        <Typography className="wr-insight-card__main">{children}</Typography>
        <Typography className="wr-insight-card__helper">{helper}</Typography>
        {action}
      </Box>
    </Box>
  );
}

function SectionTitle({ icon, title, subtitle, action }) {
  return (
    <Box className="wr-section-title">
      <Box className="wr-section-title__copy">
        {icon ? <Box className="wr-section-title__icon">{icon}</Box> : null}
        <Box>
          <Typography component="h2" className="wr-section-title__heading">{title}</Typography>
          {subtitle ? <Typography className="wr-section-title__subtitle">{subtitle}</Typography> : null}
        </Box>
      </Box>
      {action}
    </Box>
  );
}

function AttentionStudent({ student, onClick }) {
  return (
    <button type="button" className="wr-attention-row" onClick={() => onClick(student.id)}>
      <StudentAvatar student={student} />
      <Box className="wr-attention-row__identity">
        <Typography className="wr-attention-row__name">{student.name}</Typography>
        <Typography className="wr-attention-row__id">{student.studentNo}</Typography>
      </Box>
      <Box className="wr-attention-row__status">
        <StatusPill student={student} />
        <Typography>{student.note}</Typography>
      </Box>
    </button>
  );
}

function ChartCard({ title, gain, helper = 'seçili dönemde', tone = 'positive', children }) {
  return (
    <Box className="wr-chart-card">
      <Box className="wr-chart-card__header">
        <Typography component="h3">{title}</Typography>
        <Box className={`wr-gain-pill wr-gain-pill--${tone}`} role="status" aria-label={`${title}: ${gain}, ${helper}`}>
          {String(gain).startsWith('-') ? <TrendingDownRounded /> : <TrendingUpRounded />}
          <strong>{gain}</strong><span>{helper}</span>
        </Box>
      </Box>
      {children}
    </Box>
  );
}

function CompletionBar({ completed, total }) {
  const value = total ? Math.round((completed / total) * 100) : 0;
  return (
    <Box className="wr-completion-cell">
      <Box className="wr-completion-track"><span style={{ width: `${value}%` }} /></Box>
      <Typography>{completed}/{total}</Typography>
    </Box>
  );
}

function PassageRows({ rows, onOpen, ariaLabel = 'Geçmiş okuma performansları' }) {
  return (
    <TableContainer className="wr-table-wrap">
      <Table size="small" aria-label={ariaLabel}>
        <TableHead><TableRow><TableCell>#</TableCell><TableCell>Pasaj</TableCell><TableCell>Tarih</TableCell><TableCell>Tamamlanma</TableCell><TableCell>Ortalama hız</TableCell><TableCell>Ortalama doğruluk</TableCell><TableCell aria-label="Aksiyon" /></TableRow></TableHead>
        <TableBody>{rows.map((item) => (
          <TableRow key={item.assignment_id} hover tabIndex={0} onClick={() => onOpen(item)} onKeyDown={(event) => event.key === 'Enter' && onOpen(item)}>
            <TableCell>{item.sequence_position}</TableCell>
            <TableCell><strong>{item.passage_title}</strong></TableCell>
            <TableCell>{formatDate(item.assignment_week_start)} · {item.passage_code}</TableCell>
            <TableCell><CompletionBar completed={item.completed_count} total={item.student_count} /></TableCell>
            <TableCell>{item.average_words_per_minute != null ? `${item.average_words_per_minute} k/dk` : '—'}</TableCell>
            <TableCell>{item.averageAccuracy != null ? formatAccuracy(item.averageAccuracy) : '—'}</TableCell>
            <TableCell align="right"><ArrowForwardRounded fontSize="small" /></TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </TableContainer>
  );
}

function ResultActionMenu({ target, onRequestDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!target?.studentAssignmentId || target.status === 'pending') return null;

  const closeMenu = () => setAnchorEl(null);
  const requestDelete = () => {
    closeMenu();
    onRequestDelete(target);
  };

  return (
    <>
      <Tooltip title="Diğer işlemler">
        <IconButton size="small" className="wr-result-menu-button" aria-label={`${target.passageTitle} okuması için diğer işlemler`} onClick={(event) => setAnchorEl(event.currentTarget)}>
          <MoreHorizRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu} MenuListProps={{ 'aria-label': `${target.passageTitle} okuma işlemleri` }}>
        <MenuItem className="wr-result-menu-item" onClick={requestDelete}>
          <ReplayRounded fontSize="small" />
          <Box><strong>Yeniden okumaya aç</strong><span>Mevcut okuma sonucunu siler</span></Box>
        </MenuItem>
      </Menu>
    </>
  );
}

function StudentRows({ rows, assignment, onStudentClick, onRequestDelete }) {
  const orderedRows = [...rows].sort((left, right) => (
    Number(left.status === 'completed') - Number(right.status === 'completed')
  ));
  return (
    <Box className="wr-drawer-students">
      {orderedRows.map((student, index) => {
        const isCompleted = student.status === 'completed';
        const previousCompleted = index > 0 && orderedRows[index - 1].status === 'completed';
        const showHeading = index === 0 || isCompleted !== previousCompleted;
        return (
          <React.Fragment key={student.studentAssignmentId || student.id}>
            {showHeading ? <Typography className="wr-drawer-students__group">{isCompleted ? 'Tamamlayanlar' : 'Bekleyenler ve kontrol gerekenler'}</Typography> : null}
            <Box className="wr-drawer-student">
              <button type="button" className="wr-drawer-student__main" onClick={() => onStudentClick(student.id)}>
                <StudentAvatar student={student} size={42} />
                <Box className="wr-drawer-student__identity"><strong>{student.name}</strong><span>{student.studentNo}</span></Box>
                <Chip size="small" label={readingStatusLabel(student.status, student.activityStatus)} color={isCompleted ? 'success' : 'default'} />
                <Box className="wr-drawer-student__metric"><strong>{student.speed != null ? `${student.speed} k/dk` : '—'}</strong><span>Hız</span></Box>
                <Box className="wr-drawer-student__metric"><strong>{formatAccuracy(student.accuracy, student.analysisStatus, student.analysisFailureCode)}</strong><span>Doğruluk</span></Box>
                <ArrowForwardRounded fontSize="small" />
              </button>
              <ResultActionMenu
                target={{
                  studentAssignmentId: student.studentAssignmentId,
                  studentName: student.name,
                  passageTitle: assignment.passage_title,
                  weekStart: assignment.assignment_week_start,
                  assignmentId: assignment.assignment_id,
                  status: student.status,
                }}
                onRequestDelete={onRequestDelete}
              />
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

function StudentHistoryTable({ rows, student, onRequestDelete }) {
  return (
    <TableContainer className="wr-table-wrap wr-student-table">
      <Table size="small" aria-label={`${student.name} okuma geçmişi`}>
        <TableHead><TableRow><TableCell>Hafta</TableCell><TableCell>Pasaj</TableCell><TableCell>Durum</TableCell><TableCell>Okuma hızı</TableCell><TableCell>Doğruluk</TableCell><TableCell>Süre</TableCell><TableCell>Tamamlama tarihi</TableCell><TableCell align="right">İşlemler</TableCell></TableRow></TableHead>
        <TableBody>{rows.map((point) => (
          <TableRow key={point.assignment_id}>
            <TableCell>{formatDate(point.assignment_week_start)}</TableCell>
            <TableCell><strong>{point.passage_title}</strong></TableCell>
            <TableCell><Chip size="small" color={point.status === 'completed' ? 'success' : 'default'} label={readingStatusLabel(point.status, point.activity_status)} /></TableCell>
            <TableCell>{point.words_per_minute != null ? `${point.words_per_minute} k/dk` : '—'}</TableCell>
            <TableCell>{formatAccuracy(point.accuracy_pct, point.analysis_status, point.analysis_failure_code)}</TableCell>
            <TableCell>{formatDuration(point.elapsed_ms)}</TableCell>
            <TableCell>{formatDateTime(point.completed_at)}</TableCell>
            <TableCell align="right">
              <ResultActionMenu
                target={{
                  studentAssignmentId: point.student_assignment_id,
                  studentName: student.name,
                  passageTitle: point.passage_title,
                  weekStart: point.assignment_week_start,
                  assignmentId: point.assignment_id,
                  status: point.status,
                }}
                onRequestDelete={onRequestDelete}
              />
            </TableCell>
          </TableRow>
        ))}</TableBody>
      </Table>
    </TableContainer>
  );
}

export default function WeeklyReadingTrackingPage() {
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [classOverview, setClassOverview] = useState(null);
  const [assignmentDetails, setAssignmentDetails] = useState({});
  const [activeTab, setActiveTab] = useState('week');
  const [range, setRange] = useState(4);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [detailAssignmentId, setDetailAssignmentId] = useState(null);
  const [expandedPassageId, setExpandedPassageId] = useState(null);
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [attentionMode, setAttentionMode] = useState('attention');
  const [bootLoading, setBootLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [passageLoadingId, setPassageLoadingId] = useState(null);
  const [error, setError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMeCached();
        const adminUser = me?.data?.admin_user || me?.admin_user;
        const currentSchoolId = adminUser?.school_id;
        if (!currentSchoolId) throw new Error('Okul bilgisi bulunamadı.');
        const rows = await fetchClasses(currentSchoolId);
        if (cancelled) return;
        const ordered = (Array.isArray(rows) ? rows : [])
          .filter((item) => item?.id != null && classLabel(item))
          .sort((left, right) => classLabel(left).localeCompare(classLabel(right), 'tr-TR'));
        setSchoolId(currentSchoolId);
        setClasses(ordered);
        setSelectedClassId(ordered[0]?.id != null ? String(ordered[0].id) : '');
        if (!ordered.length) setError('Bu okul için gösterilecek aktif sınıf bulunamadı.');
      } catch (requestError) {
        if (!cancelled) setError(requestError?.message || 'Sınıflar yüklenemedi.');
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!schoolId || !selectedClassId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const payload = await fetchWeeklyReadingOverview(schoolId, selectedClassId);
        if (cancelled) return;
        const overview = Array.isArray(payload) ? payload[0] || null : null;
        setClassOverview(overview);
        setAssignmentDetails({});
        setDetailAssignmentId(null);
        setDetailError('');
        setSelectedStudentId((current) => (
          overview?.student_trends?.some((student) => String(student.student_id) === String(current))
            ? current
            : String(overview?.student_trends?.[0]?.student_id || '')
        ));
      } catch (requestError) {
        if (!cancelled) {
          setClassOverview(null);
          setError(requestError?.message || 'Haftalık okuma durumu alınamadı.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, selectedClassId, reloadKey]);

  const selectedClass = classes.find((item) => String(item.id) === String(selectedClassId));
  const assignments = useMemo(() => (classOverview?.assignments || []).map((assignment) => ({
    ...assignment,
    averageAccuracy: getAssignmentAccuracy(classOverview, assignment.assignment_id),
  })), [classOverview]);
  const latestAssignment = assignments[0] || null;
  const previousAssignment = assignments[1] || null;
  const latestAccuracy = latestAssignment?.averageAccuracy ?? null;
  const historicalAssignments = assignments.slice(1, 5);
  const speedComparison = buildPerformanceComparison({
    metricName: 'Okuma hızı',
    currentValue: latestAssignment?.average_words_per_minute,
    historicalValues: historicalAssignments.map((assignment) => assignment.average_words_per_minute),
    lastWeekValue: previousAssignment?.average_words_per_minute,
    threshold: 2,
    unit: 'k/dk',
    positiveTone: 'green',
  });
  const accuracyComparison = buildPerformanceComparison({
    metricName: 'Doğruluk',
    currentValue: latestAccuracy,
    historicalValues: historicalAssignments.map((assignment) => assignment.averageAccuracy),
    lastWeekValue: previousAssignment?.averageAccuracy,
    threshold: 1,
    unit: 'yüzde puan',
    lowThreshold: 80,
  });
  const currentStudents = useMemo(() => {
    if (!latestAssignment) return [];
    return (classOverview?.student_trends || []).map((trend) => buildStudentView(trend, latestAssignment.assignment_id));
  }, [classOverview, latestAssignment]);
  const attentionStudents = currentStudents.filter((student) => student.insightTone !== 'success');
  const waitingStudents = currentStudents.filter((student) => student.status !== 'completed');
  const visibleAttentionStudents = attentionMode === 'waiting' ? waitingStudents : attentionStudents;
  const selectedTrend = classOverview?.student_trends?.find((student) => String(student.student_id) === String(selectedStudentId)) || null;
  const selectedStudent = selectedTrend && latestAssignment ? buildStudentView(selectedTrend, latestAssignment.assignment_id) : null;
  const selectedStudentRows = [...(selectedTrend?.points || [])].sort((left, right) => new Date(right.assignment_week_start) - new Date(left.assignment_week_start));
  const selectedTrendChronological = [...(selectedTrend?.points || [])].sort((left, right) => new Date(left.assignment_week_start) - new Date(right.assignment_week_start));
  const studentSpeedGain = selectedTrendChronological.length > 1 && selectedTrendChronological.at(-1)?.words_per_minute != null && selectedTrendChronological[0]?.words_per_minute != null
    ? Number((selectedTrendChronological.at(-1).words_per_minute - selectedTrendChronological[0].words_per_minute).toFixed(1)) : null;
  const studentAccuracyGain = selectedTrendChronological.length > 1 && selectedTrendChronological.at(-1)?.accuracy_pct != null && selectedTrendChronological[0]?.accuracy_pct != null
    ? Number((selectedTrendChronological.at(-1).accuracy_pct - selectedTrendChronological[0].accuracy_pct).toFixed(1)) : null;

  const classTrendData = useMemo(() => {
    const chronologicalAssignments = [...assignments].sort((left, right) => new Date(left.assignment_week_start) - new Date(right.assignment_week_start));
    return chronologicalAssignments.slice(-range).map((assignment) => ({
      assignmentId: assignment.assignment_id,
      label: formatDate(assignment.assignment_week_start, { withYear: false }),
      speed: assignment.average_words_per_minute,
      accuracy: assignment.averageAccuracy,
    }));
  }, [assignments, range]);

  const detailAssignment = assignments.find((assignment) => String(assignment.assignment_id) === String(detailAssignmentId)) || null;
  const detail = detailAssignmentId ? assignmentDetails[detailAssignmentId] : null;
  const detailStudentRows = useMemo(() => {
    if (!detailAssignment) return [];
    const detailByStudentId = new Map((detail?.students || []).map((student) => [String(student.student_id), student]));
    return (classOverview?.student_trends || []).map((trend) => (
      buildStudentView(trend, detailAssignment.assignment_id, detailByStudentId.get(String(trend.student_id)))
    ));
  }, [classOverview, detail, detailAssignment]);

  const loadAssignmentDetail = async (assignmentId) => {
    if (!schoolId || assignmentDetails[assignmentId] || detailLoadingId === assignmentId) return;
    try {
      setDetailLoadingId(assignmentId);
      setDetailError('');
      const payload = await fetchWeeklyReadingAssignment(schoolId, assignmentId);
      setAssignmentDetails((current) => ({ ...current, [assignmentId]: payload }));
    } catch (requestError) {
      setDetailError(requestError?.message || 'Pasaj ayrıntıları alınamadı.');
    } finally {
      setDetailLoadingId(null);
    }
  };

  const openAssignment = (assignment) => {
    setExpandedPassageId(null);
    setDetailAssignmentId(assignment.assignment_id);
    loadAssignmentDetail(assignment.assignment_id);
  };

  const togglePassageBody = async (assignmentId) => {
    if (String(expandedPassageId) === String(assignmentId)) {
      setExpandedPassageId(null);
      return;
    }
    if (assignmentDetails[assignmentId]?.passage_body) {
      setExpandedPassageId(assignmentId);
      return;
    }
    if (!schoolId || passageLoadingId === assignmentId) return;
    try {
      setPassageLoadingId(assignmentId);
      setDetailError('');
      const payload = await fetchWeeklyReadingAssignment(schoolId, assignmentId, { includePassageBody: true });
      setAssignmentDetails((current) => ({ ...current, [assignmentId]: payload }));
      setExpandedPassageId(assignmentId);
    } catch (requestError) {
      setDetailError(requestError?.message || 'Okuma metni alınamadı.');
    } finally {
      setPassageLoadingId(null);
    }
  };

  const openStudent = (studentId) => {
    setSelectedStudentId(String(studentId));
    setDetailAssignmentId(null);
    setAttentionOpen(false);
    setActiveTab('students');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openAttention = (mode) => {
    setAttentionMode(mode);
    setAttentionOpen(true);
  };

  const requestResultDelete = (target) => {
    setDeleteError('');
    setDeleteTarget(target);
  };

  const handleDeleteResult = async () => {
    if (!deleteTarget || !schoolId) return;
    const target = deleteTarget;
    try {
      setDeleting(true);
      setDeleteError('');
      await deleteWeeklyReadingResult(schoolId, target.studentAssignmentId);
    } catch (requestError) {
      setDeleteError(requestError?.message || 'Okuma sonucu silinemedi.');
      setDeleting(false);
      return;
    }

    setDeleteTarget(null);
    if (String(expandedPassageId) === String(target.assignmentId)) setExpandedPassageId(null);
    setSuccessMessage(`${target.studentName} için “${target.passageTitle}” yeniden okumaya açıldı.`);
    setReloadKey((value) => value + 1);

    if (String(detailAssignmentId) === String(target.assignmentId)) {
      try {
        const payload = await fetchWeeklyReadingAssignment(schoolId, target.assignmentId);
        setAssignmentDetails((current) => ({ ...current, [target.assignmentId]: payload }));
      } catch (requestError) {
        setDetailError(requestError?.message || 'Güncel öğrenci listesi alınamadı.');
      }
    }
    setDeleting(false);
  };

  const completion = Number(latestAssignment?.completion_pct || 0);

  return (
    <Box className="weekly-reading-page wr-v1-reading" data-testid="weekly-reading-typography">
      <main className="wr-main">
        <Box className="wr-page-heading">
          <Box className="wr-page-heading__title"><BookMark /><Box><Typography component="h1">Haftalık Okumalar</Typography><Typography>Otomatik atanan pasajların sınıf ve öğrenci durumlarını izleyin.</Typography></Box></Box>
          <Box className="wr-page-actions">
            <FormControl size="small" className="wr-class-select" disabled={bootLoading || !classes.length}>
              <InputLabel id="class-select-label">Sınıf</InputLabel>
              <Select labelId="class-select-label" value={selectedClassId} label="Sınıf" onChange={(event) => setSelectedClassId(event.target.value)}>
                {classes.map((item) => <MenuItem value={String(item.id)} key={item.id}>{classLabel(item)}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" className="wr-refresh" startIcon={<RefreshRounded className={loading ? 'is-spinning' : ''} />} onClick={() => setReloadKey((value) => value + 1)} disabled={loading || !selectedClassId}>{loading ? 'Yenileniyor' : 'Yenile'}</Button>
          </Box>
        </Box>

        {error ? <Alert severity="error" sx={{ maxWidth: 1220, mx: 'auto', mb: 1.5 }}>{error}</Alert> : null}
        {bootLoading || loading ? (
          <Box className="wr-loading" aria-label="Veriler yükleniyor">
            <Skeleton variant="rounded" height={174} />
            <Stack direction="row" gap={1.5}><Skeleton variant="rounded" height={112} sx={{ flex: 1 }} /><Skeleton variant="rounded" height={112} sx={{ flex: 1 }} /><Skeleton variant="rounded" height={112} sx={{ flex: 1 }} /></Stack>
          </Box>
        ) : latestAssignment ? (
          <>
            <section className="wr-hero" aria-labelledby="current-reading-title">
              <img className="wr-hero__image" src={DEFAULT_READING_IMAGE} alt="Açık bir kitaptan yükselen hayal ve keşif dünyası" />
              <Box className="wr-hero__content">
                <Typography className="wr-eyebrow">Bu Haftanın Okuması</Typography>
                <Typography component="h2" id="current-reading-title">{latestAssignment.passage_title}</Typography>
                <Typography className="wr-hero__meta">{formatDate(latestAssignment.assignment_week_start)} · {latestAssignment.passage_code}</Typography>
                <LinearProgress variant="determinate" value={completion} className="wr-hero__progress" />
                <Typography className="wr-hero__completion"><strong>{latestAssignment.completed_count} / {latestAssignment.student_count}</strong> öğrenci tamamladı</Typography>
              </Box>
              <Box className="wr-hero__metrics">
                <Chip className="wr-active-chip" icon={<CalendarMonthRounded />} label="Bu hafta aktif" />
                <Box className="wr-hero__metric-grid wr-hero__metric-grid--two">
                  <MiniMetric icon={<SpeedRounded />} tone="green" value={latestAssignment.average_words_per_minute != null ? `${latestAssignment.average_words_per_minute} k/dk` : '—'} label="ortalama hız" />
                  <MiniMetric icon={<TaskAltRounded />} tone="purple" value={latestAccuracy != null ? formatAccuracy(latestAccuracy) : '—'} label="ortalama doğruluk" />
                </Box>
                <Button variant="contained" endIcon={<ArrowForwardRounded />} onClick={() => openAssignment(latestAssignment)}>Öğrencileri Gör</Button>
              </Box>
            </section>

            <section className="wr-insight-grid" aria-label="Haftalık özetler">
              <InsightCard tone={speedComparison.tone} eyebrow="Okuma performansı" helper={speedComparison.helper} icon={<BarChartRounded />}>
                {speedComparison.main}
              </InsightCard>
              <InsightCard tone={accuracyComparison.tone} eyebrow="Doğruluk gelişimi" helper={accuracyComparison.helper} icon={<TaskAltRounded />}>
                {accuracyComparison.main}
              </InsightCard>
              <InsightCard
                tone={latestAssignment.pending_count ? 'orange' : 'green'}
                eyebrow="Bugün için"
                helper={latestAssignment.pending_count ? 'Küçük bir hatırlatma faydalı olabilir.' : 'Harika, sınıf bu haftayı tamamladı!'}
                icon={latestAssignment.pending_count ? <LightbulbRounded /> : <CheckCircleRounded />}
                action={latestAssignment.pending_count ? <Button className="wr-insight-card__action" endIcon={<ArrowForwardRounded />} onClick={() => openAttention('waiting')}>Bekleyenleri gör</Button> : null}
              >
                <strong>{latestAssignment.pending_count ? `${latestAssignment.pending_count} öğrenci henüz okumayı tamamlamadı.` : 'Tüm öğrenciler okumayı tamamladı.'}</strong>
              </InsightCard>
            </section>
          </>
        ) : !error ? (
          <Box className="wr-panel wr-empty" sx={{ maxWidth: 1220, mx: 'auto' }}><MenuBookRounded /><Typography component="h2">Bu sınıf için henüz haftalık okuma ataması bulunmuyor.</Typography><Typography>{classLabel(selectedClass)} sınıfına bir pasaj atandığında bilgiler burada görünecek.</Typography></Box>
        ) : null}

        <Box className="wr-tabs" role="tablist" aria-label="Haftalık okuma görünümleri">
          {[['week', 'Bu Hafta'], ['students', 'Öğrenciler'], ['history', 'Geçmiş']].map(([value, label]) => <button type="button" role="tab" aria-selected={activeTab === value} className={activeTab === value ? 'is-active' : ''} onClick={() => setActiveTab(value)} key={value}>{label}</button>)}
        </Box>

        {activeTab === 'week' && latestAssignment ? (
          <Box className="wr-week-view">
            <section className="wr-panel wr-attention-panel wr-attention-panel--wide">
              <SectionTitle icon={<WarningAmberRounded />} title="Dikkat Gerektirenler" subtitle="Bu hafta kısa bir kontrol faydalı olabilir." action={<Button endIcon={<ArrowForwardRounded />} onClick={() => openAttention('attention')} disabled={!attentionStudents.length}>Tümünü Gör</Button>} />
              {attentionStudents.length ? <Box className="wr-attention-list">{attentionStudents.slice(0, 3).map((student) => <AttentionStudent student={student} onClick={openStudent} key={student.id} />)}</Box> : <Box className="wr-calm-state"><CheckCircleRounded /><Typography>Bu hafta dikkat gerektiren özel bir durum görünmüyor.</Typography></Box>}
            </section>

            <section className="wr-panel wr-recent-panel">
              <SectionTitle icon={<Box className="wr-clock-icon">◷</Box>} title="Son okumalar" subtitle="Mevcut hafta dahil en son 10 okumanın sınıf performansı" action={<Button endIcon={<ArrowForwardRounded />} onClick={() => setActiveTab('history')} disabled={assignments.length < 2}>Tüm geçmişi gör</Button>} />
              {assignments.length ? <PassageRows rows={assignments.slice(0, 10)} onOpen={openAssignment} ariaLabel="Son okuma performansları" /> : <Box className="wr-calm-state"><MenuBookRounded /><Typography>Henüz okuma verisi bulunmuyor.</Typography></Box>}
            </section>
          </Box>
        ) : null}

        {activeTab === 'students' ? (
          <section className="wr-panel wr-student-view">
            <SectionTitle icon={<PeopleAltRounded />} title="Öğrenci gelişimi" subtitle="Öğrencilerin zaman içindeki okuma hızını ve doğruluğunu karşılaştırın." action={classOverview?.student_trends?.length ? <FormControl size="small" className="wr-student-select"><InputLabel id="student-select-label">Öğrenci</InputLabel><Select labelId="student-select-label" label="Öğrenci" value={selectedStudentId} onChange={(event) => setSelectedStudentId(event.target.value)}>{classOverview.student_trends.map((student) => <MenuItem value={String(student.student_id)} key={student.student_id}>{student.student_no} · {student.student_name}</MenuItem>)}</Select></FormControl> : null} />
            {selectedStudent ? <>
              <Box className="wr-student-summary">
                <StudentAvatar student={selectedStudent} size={62} />
                <Box className="wr-student-summary__identity"><Typography component="h3">{selectedStudent.name}</Typography><Typography>{selectedStudent.studentNo} · {classLabel(selectedClass)} sınıfı</Typography></Box>
                <Box className="wr-student-summary__metric"><span>Son durum</span><StatusPill student={selectedStudent} /></Box>
                <Box className="wr-student-summary__metric"><span>Son hız</span><strong>{selectedStudent.speed != null ? `${selectedStudent.speed} k/dk` : '—'}</strong></Box>
                <Box className="wr-student-summary__metric"><span>Son doğruluk</span><strong>{formatAccuracy(selectedStudent.accuracy, selectedStudent.analysisStatus, selectedStudent.analysisFailureCode)}</strong></Box>
                <Box className="wr-student-summary__metric"><span>Tamamladığı</span><strong>{selectedStudentRows.filter((point) => point.status === 'completed').length} okuma</strong></Box>
              </Box>
              {selectedStudentRows.length ? <>
                <Box className="wr-student-chart-grid">
                  <ChartCard title="Okuma hızı gelişimi" gain={studentSpeedGain == null ? '—' : `${studentSpeedGain > 0 ? '+' : ''}${studentSpeedGain} k/dk`}><LineChart height={240} xAxis={[{ scaleType: 'point', data: selectedTrendChronological.map((point) => formatDate(point.assignment_week_start, { withYear: false })) }]} series={[{ data: selectedTrendChronological.map((point) => point.words_per_minute), color: '#0875f5', curve: 'monotoneX', connectNulls: true, showMark: true }]} slotProps={{ legend: { hidden: true } }} grid={{ horizontal: true }} /></ChartCard>
                  <ChartCard title="Doğruluk gelişimi" gain={studentAccuracyGain == null ? '—' : `${studentAccuracyGain > 0 ? '+' : ''}%${studentAccuracyGain}`}><LineChart height={240} xAxis={[{ scaleType: 'point', data: selectedTrendChronological.map((point) => formatDate(point.assignment_week_start, { withYear: false })) }]} yAxis={[{ min: 0, max: 100 }]} series={[{ data: selectedTrendChronological.map((point) => point.accuracy_pct), color: '#18a875', curve: 'monotoneX', connectNulls: true, showMark: true }]} slotProps={{ legend: { hidden: true } }} grid={{ horizontal: true }} /></ChartCard>
                </Box>
                <StudentHistoryTable rows={selectedStudentRows} student={selectedStudent} onRequestDelete={requestResultDelete} />
              </> : <Box className="wr-empty"><MenuBookRounded /><Typography component="h3">Henüz okuma geçmişi yok</Typography><Typography>Bu öğrenci bir okumayı tamamladığında gelişim grafikleri burada görünecek.</Typography></Box>}
            </> : <Box className="wr-empty"><PeopleAltRounded /><Typography component="h3">Bu sınıfta gösterilecek öğrenci verisi bulunmuyor.</Typography></Box>}
          </section>
        ) : null}

        {activeTab === 'history' ? (
          <Box className="wr-history-view">
            <section className="wr-panel wr-progress-panel">
              <SectionTitle icon={<AutoGraphRounded />} title="Sınıfın gelişimi" subtitle="Bu haftanın performansını önceki okumalarla karşılaştırın." action={<FormControl size="small" className="wr-range-select"><Select value={range} onChange={(event) => setRange(Number(event.target.value))} inputProps={{ 'aria-label': 'Grafik tarih aralığı' }} startAdornment={<CalendarMonthRounded />}><MenuItem value={4}>Son 4 hafta</MenuItem><MenuItem value={8}>Son 8 hafta</MenuItem><MenuItem value={12}>Son 12 hafta</MenuItem></Select></FormControl>} />
              {classTrendData.length ? <Box className="wr-chart-grid">
                <ChartCard title="Okuma hızı gelişimi" gain={speedComparison.delta == null ? '—' : `${speedComparison.delta > 0 ? '+' : ''}${formatMetricNumber(speedComparison.delta)} k/dk`} helper={speedComparison.baselineLabel} tone={speedComparison.delta == null ? 'neutral' : speedComparison.delta < 0 ? 'negative' : 'positive'}><LineChart height={220} margin={{ top: 18, right: 16, bottom: 32, left: 42 }} xAxis={[{ scaleType: 'point', data: classTrendData.map((item) => item.label), tickLabelStyle: { fontSize: 10 } }]} yAxis={[{ min: 0, label: 'k/dk', tickLabelStyle: { fontSize: 10 } }]} series={[{ data: classTrendData.map((item) => item.speed), color: '#0875f5', curve: 'monotoneX', connectNulls: true, showMark: true, area: true }]} grid={{ horizontal: true, vertical: true }} slotProps={{ legend: { hidden: true } }} /></ChartCard>
                <ChartCard title="Doğruluk gelişimi" gain={accuracyComparison.delta == null ? '—' : `${accuracyComparison.delta > 0 ? '+' : ''}${formatMetricNumber(accuracyComparison.delta)} yüzde puan`} helper={accuracyComparison.baselineLabel} tone={accuracyComparison.delta == null ? 'neutral' : accuracyComparison.delta < 0 ? 'negative' : 'positive'}><LineChart height={220} margin={{ top: 18, right: 16, bottom: 32, left: 42 }} xAxis={[{ scaleType: 'point', data: classTrendData.map((item) => item.label), tickLabelStyle: { fontSize: 10 } }]} yAxis={[{ min: 0, max: 100, label: '%', tickLabelStyle: { fontSize: 10 } }]} series={[{ data: classTrendData.map((item) => item.accuracy), color: '#7a3ff2', curve: 'monotoneX', connectNulls: true, showMark: true, area: true }]} grid={{ horizontal: true, vertical: true }} slotProps={{ legend: { hidden: true } }} /></ChartCard>
              </Box> : <Box className="wr-calm-state"><AutoGraphRounded /><Typography>Gelişim grafikleri için henüz yeterli okuma verisi yok.</Typography></Box>}
            </section>

            <section className="wr-panel wr-history-list-panel">
              <SectionTitle icon={<CalendarMonthRounded />} title="Geçmiş okumalar" subtitle="Önceki pasajların sınıf performansını karşılaştırın ve öğrenci detaylarını inceleyin." />
              {assignments.slice(1).length ? <PassageRows rows={assignments.slice(1)} onOpen={openAssignment} /> : <Box className="wr-empty"><MenuBookRounded /><Typography component="h3">Henüz geçmiş okuma verisi bulunmuyor.</Typography></Box>}
            </section>
          </Box>
        ) : null}
      </main>

      <Drawer anchor="right" open={Boolean(detailAssignmentId)} onClose={() => setDetailAssignmentId(null)} PaperProps={{ className: 'wr-detail-drawer wr-v1-drawer', role: 'dialog', 'aria-label': 'Pasaj öğrenci durumu' }}>
        {detailAssignment ? <Box>
          <Box className="wr-drawer-header"><Box><Typography className="wr-eyebrow">Pasaj öğrenci durumu</Typography><Typography component="h2">{detailAssignment.passage_title}</Typography><Typography>{formatDate(detailAssignment.assignment_week_start)} · {detailAssignment.passage_code}</Typography></Box><IconButton onClick={() => setDetailAssignmentId(null)} aria-label="Detayı kapat"><CloseRounded /></IconButton></Box>
          <Box className="wr-drawer-summary"><MiniMetric icon={<CheckCircleRounded />} tone="green" value={`${detailAssignment.completed_count}/${detailAssignment.student_count}`} label="öğrenci tamamladı" /><MiniMetric icon={<Groups2Rounded />} tone="orange" value={detailAssignment.pending_count} label="öğrenci bekliyor" /></Box>
          {detailLoadingId === detailAssignment.assignment_id ? <LinearProgress sx={{ mx: 2, mb: 2 }} /> : null}
          {detailError ? <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{detailError}</Alert> : null}
          <Box className={`wr-passage-disclosure ${String(expandedPassageId) === String(detailAssignment.assignment_id) ? 'is-expanded' : ''}`}>
            <Box className="wr-passage-disclosure__summary">
              <Box className="wr-passage-disclosure__copy"><Box className="wr-passage-disclosure__icon"><MenuBookRounded /></Box><Box><strong>Okuma parçası</strong><span>İhtiyaç duyduğunuzda pasaj metnini görüntüleyin.</span></Box></Box>
              <Button
                variant="text"
                endIcon={String(expandedPassageId) === String(detailAssignment.assignment_id) ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                onClick={() => togglePassageBody(detailAssignment.assignment_id)}
                disabled={detailLoadingId === detailAssignment.assignment_id || passageLoadingId === detailAssignment.assignment_id}
              >
                {passageLoadingId === detailAssignment.assignment_id ? 'Metin yükleniyor…' : String(expandedPassageId) === String(detailAssignment.assignment_id) ? 'Metni gizle' : 'Okuma metnini göster'}
              </Button>
            </Box>
            {String(expandedPassageId) === String(detailAssignment.assignment_id) ? <Box className="wr-passage-note"><p>{detail?.passage_body || 'Bu pasaj için gösterilecek metin bulunmuyor.'}</p></Box> : null}
          </Box>
          {detailStudentRows.length ? <StudentRows rows={detailStudentRows} assignment={detailAssignment} onStudentClick={openStudent} onRequestDelete={requestResultDelete} /> : !detailLoadingId ? <Box className="wr-empty"><PeopleAltRounded /><Typography>Bu pasaj için gösterilecek öğrenci verisi bulunmuyor.</Typography></Box> : null}
        </Box> : null}
      </Drawer>

      <Drawer anchor="right" open={attentionOpen} onClose={() => setAttentionOpen(false)} PaperProps={{ className: 'wr-detail-drawer wr-v1-drawer', role: 'dialog', 'aria-label': attentionMode === 'waiting' ? 'Okumayı bekleyen öğrenciler' : 'Dikkat gerektiren öğrenciler' }}>
        <Box className="wr-drawer-header"><Box><Typography className="wr-eyebrow wr-text--orange">Bu hafta</Typography><Typography component="h2">{attentionMode === 'waiting' ? 'Okumayı Bekleyen Öğrenciler' : 'Dikkat Gerektirenler'}</Typography><Typography>{attentionMode === 'waiting' ? 'Bu öğrencilerin haftalık okuması henüz tamamlanmadı.' : 'Bu işaretler gerçek ölçümlerden üretilen açıklanabilir kontrol noktalarıdır.'}</Typography></Box><IconButton onClick={() => setAttentionOpen(false)} aria-label="Listeyi kapat"><CloseRounded /></IconButton></Box>
        <Box className="wr-all-attention">{visibleAttentionStudents.map((student) => <AttentionStudent student={student} onClick={openStudent} key={student.id} />)}</Box>
      </Drawer>

      <Dialog open={Boolean(deleteTarget)} onClose={() => (deleting ? null : setDeleteTarget(null))} fullWidth maxWidth="xs" PaperProps={{ className: 'wr-v1-delete' }} aria-labelledby="wr-delete-result-title">
        <DialogTitle id="wr-delete-result-title" className="wr-delete-dialog__title">Okuma sonucunu yeniden aç</DialogTitle>
        <DialogContent className="wr-delete-dialog__content">
          <Typography>
            Bu öğrencinin seçili okuma sonucu kalıcı olarak silinecek. Öğrenci pasajı yeniden okuyabilecek ve yeni bir sonuç oluşturabilecek. Bu işlem geri alınamaz.
          </Typography>
          {deleteTarget ? <Box className="wr-delete-dialog__summary"><strong>{deleteTarget.studentName}</strong><span>{deleteTarget.passageTitle}</span><small>{formatDate(deleteTarget.weekStart)}</small></Box> : null}
          {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
        </DialogContent>
        <DialogActions className="wr-delete-dialog__actions">
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Vazgeç</Button>
          <Button variant="contained" startIcon={<ReplayRounded />} onClick={handleDeleteResult} disabled={deleting}>{deleting ? 'İşleniyor…' : 'Sil ve yeniden aç'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(successMessage)} autoHideDuration={4500} onClose={() => setSuccessMessage('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSuccessMessage('')}>{successMessage}</Alert>
      </Snackbar>
    </Box>
  );
}
